import React, { useEffect, useMemo, useState } from "react";

export const ListingGroupCard = ({ cardKey, className = "", children }) => (
  <div className="min-w-0 max-w-full">
    <div
      key={cardKey}
      className={`listing-group-card mb-5 fish-item bg-gray-50 p-3 rounded-lg shadow hover:bg-indigo-50 touch:bg-indigo-50 transition-colors min-w-0 max-w-full ${className}`.trim()}
    >
      {children}
    </div>
  </div>
);

export const ListingPreviewTable = ({ rows, renderRow }) => (
  <div className="listing-preview-table-wrapper max-w-full overflow-x-auto">
    <table className="listing-preview-table min-w-full text-sm">
      <tbody>{rows.map(renderRow)}</tbody>
    </table>
  </div>
);

const listingTabClass = (isActive) =>
  `flex-1 flex flex-row flex-nowrap items-center justify-center gap-1.5 py-2 px-3 text-center text-sm font-medium transition-colors ${
    isActive
      ? "border-b-2 border-blue-600 text-blue-600 bg-blue-50"
      : "text-gray-600 hover:text-blue-600 hover:bg-gray-50"
  }`;

export const ListingGroupFrame = ({
  cardKey,
  onHeaderClick,
  title,
  subtitle,
  headerActions = null,
  summaryLabel,
  summaryValue,
  summaryBadge = null,
  mobileSummaryValue = null,
  previewRows,
  renderPreviewRow,
  imageContent = null,
  imageWrapperClassName = "",
  headerContent = null,
  cardClassName = "",
  tabs = [],
  children = null,
}) => {
  const visibleTabs = tabs.filter(Boolean);
  const hasVisibleImage = Boolean(imageContent) && !imageWrapperClassName
    .split(/\s+/)
    .includes("hidden");

  return (
    <ListingGroupCard cardKey={cardKey} className={cardClassName}>
      {!headerContent && (
        <div
          className={onHeaderClick ? "cursor-pointer" : undefined}
          onClick={onHeaderClick}
        >
        <div
          className={`flex fish-listing-header-flex flex-row flex-wrap justify-between gap-3 mb-4 min-w-0 ${
            hasVisibleImage ? "listing-header-has-image" : ""
          }`.trim()}
        >
          <div className="listing-header-copy flex-1 min-w-0">
            <div className="flex flex-col justify-between h-full">
              <div className="listing-header-title-row flex flex-row justify-between gap-2 min-w-0">
                <div className="flex-auto min-w-0">
                  <h2 className="listing-header-title text-lg font-semibold mb-1 text-blue-600 break-words">
                    {title}
                  </h2>
                  {subtitle && (
                    <div className="text-sm text-gray-600 mb-2 break-words">
                      {subtitle}
                    </div>
                  )}
                </div>
                {headerActions && (
                  <div className="listing-header-actions-slot flex-none min-w-[min-content] pb-1">
                    {headerActions}
                  </div>
                )}
              </div>
              {(summaryLabel || summaryValue) && (
                <div className="listing-header-summary flex flex-row justify-between mb-2 gap-2 text-sm text-gray-600 font-bold min-w-0">
                  <span
                    className={`flex-shrink-0 ${
                      mobileSummaryValue ? "hidden-on-mobile" : ""
                    }`.trim()}
                  >
                    {summaryLabel}
                  </span>
                  <span
                    className={`inline-flex flex-wrap items-center justify-end gap-2 text-right min-w-0 break-words ${
                      mobileSummaryValue ? "hidden-on-mobile" : ""
                    }`.trim()}
                  >
                    {summaryValue}
                    {summaryBadge}
                  </span>
                  {mobileSummaryValue && (
                    <span className="listing-header-mobile-summary visible-on-mobile min-w-0 flex-wrap items-center justify-end gap-2">
                      {mobileSummaryValue}
                      {summaryBadge}
                    </span>
                  )}
                </div>
              )}
              {previewRows && renderPreviewRow && (
                <ListingPreviewTable
                  rows={previewRows}
                  renderRow={renderPreviewRow}
                />
              )}
            </div>
          </div>

          {imageContent && (
            <div className={`listing-header-image-wrapper flex-1 min-w-0 self-stretch ${imageWrapperClassName}`.trim()}>
              <div className="listing-header-image-panel relative flex h-full min-h-60 flex-col">
                {imageContent}
              </div>
            </div>
          )}
        </div>
        </div>
      )}

      {headerContent}

      {visibleTabs.length > 0 && (
        <>
          <hr className="listing-group-tabs-divider mt-3" />
          <div className="listing-group-tabs flex flex-row border-b border-gray-300">
            {visibleTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                aria-label={tab.label || undefined}
                onClick={(event) => {
                  event.stopPropagation();
                  tab.onClick?.(event);
                }}
                className={listingTabClass(tab.isActive)}
              >
                {tab.label && (
                  <span className="hidden-on-mobile whitespace-nowrap">{tab.label}</span>
                )}
                {tab.icon}
              </button>
            ))}
          </div>
        </>
      )}

      {children}
    </ListingGroupCard>
  );
};

const isMissingSortValue = (value) =>
  value === null || value === undefined || value === "";

const getColumnSortValue = (column, row) => {
  if (typeof column.sortValue === "function") return column.sortValue(row);
  if (column.sortKey) return row?.[column.sortKey];
  return row?.[column.key];
};

const compareSortValues = (a, b, direction) => {
  const aMissing = isMissingSortValue(a);
  const bMissing = isMissingSortValue(b);
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;

  const aNumber = Number(a);
  const bNumber = Number(b);
  const rawComparison =
    Number.isFinite(aNumber) && Number.isFinite(bNumber)
      ? aNumber - bNumber
      : String(a).localeCompare(String(b), undefined, {
          numeric: true,
          sensitivity: "base",
        });

  return direction === "asc" ? rawComparison : -rawComparison;
};

const getSortIndicator = (isActive, direction) => {
  if (!isActive) return "";
  return direction === "asc" ? "\u25B2" : "\u25BC";
};

export const ListingDetailsTable = ({
  columns,
  rows,
  renderRow,
  emptyContent = null,
  initialSortKey = null,
  initialSortDirection = "asc",
  sortConfig: controlledSortConfig = null,
  sortColumns = [],
  onSortChange = null,
  desktopInitialRowCount = null,
  mobileInitialRowCount = null,
}) => {
  const [internalSortConfig, setInternalSortConfig] = useState(
    initialSortKey
      ? { key: initialSortKey, direction: initialSortDirection }
      : null
  );
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 768
  );
  const [showAllRows, setShowAllRows] = useState(false);
  const sortConfig = controlledSortConfig || internalSortConfig;
  const mobileColumns = columns.filter(
    (column) => !["title", "listing", "product"].includes(column.key)
  );

  const sortedRows = useMemo(() => {
    if (!sortConfig?.key) return rows;
    const column = [...columns, ...sortColumns].find(
      (candidate) => candidate.key === sortConfig.key
    );
    if (!column?.sortable) return rows;

    return rows
      .map((row, index) => ({ row, index }))
      .sort((a, b) => {
        const comparison = compareSortValues(
          getColumnSortValue(column, a.row),
          getColumnSortValue(column, b.row),
          sortConfig.direction
        );
        return comparison || a.index - b.index;
      })
      .map(({ row }) => row);
  }, [columns, rows, sortColumns, sortConfig]);

  useEffect(() => {
    const updateViewport = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  const initialRowCount = isMobile
    ? mobileInitialRowCount ?? desktopInitialRowCount
    : desktopInitialRowCount;
  const hasRowLimit = Number.isInteger(initialRowCount) && initialRowCount > 0;
  const visibleRows =
    hasRowLimit && !showAllRows
      ? sortedRows.slice(0, initialRowCount)
      : sortedRows;
  const hiddenRowCount = Math.max(sortedRows.length - visibleRows.length, 0);
  const canToggleRows = hasRowLimit && sortedRows.length > initialRowCount;

  const handleSort = (column) => {
    if (!column.sortable) return;
    const defaultDirection = column.defaultSortDirection || "asc";
    const nextSortConfig = {
      key: column.key,
      direction:
        sortConfig?.key === column.key
          ? sortConfig.direction === "asc"
            ? "desc"
            : "asc"
          : defaultDirection,
    };

    if (onSortChange) {
      onSortChange(nextSortConfig);
    } else {
      setInternalSortConfig(nextSortConfig);
    }
    setShowAllRows(false);
  };

  return (
    <div className="overflow-x-auto rounded-lg shadow border border-gray-200">
      {mobileColumns.length > 0 && (
        <div className="listing-mobile-table-header visible-on-mobile" aria-hidden="true">
          {mobileColumns.map((column) => (
            <span key={column.key}>{column.mobileLabel || column.label}</span>
          ))}
        </div>
      )}
      <table className="w-full bg-white table-fixed listing-table-responsive">
        <thead className="bg-blue-100">
          <tr>
            {columns.map((column) => {
              const isActive = sortConfig?.key === column.key;
              const ariaSort = column.sortable
                ? isActive
                  ? sortConfig.direction === "asc"
                    ? "ascending"
                    : "descending"
                  : "none"
                : undefined;

              return (
                <th
                  key={column.key}
                  aria-sort={ariaSort}
                  className={`${column.width || ""} py-1.5 px-2 ${column.align || "text-left"} font-medium text-gray-700 text-xs`}
                >
                  {column.sortable ? (
                    <button
                      type="button"
                      onClick={() => handleSort(column)}
                      className={`inline-flex w-full items-center gap-1 ${column.align === "text-right" ? "justify-end" : column.align === "text-center" ? "justify-center" : "justify-start"} font-medium text-gray-700 cursor-pointer`}
                    >
                      <span>{column.label}</span>
                      <span aria-hidden="true" className="min-w-[0.75rem]">
                        {getSortIndicator(isActive, sortConfig?.direction)}
                      </span>
                    </button>
                  ) : (
                    column.label
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>{visibleRows.map(renderRow)}</tbody>
      </table>
      {sortedRows.length === 0 && emptyContent}
      {canToggleRows && (
        <div className="listing-table-toggle border-t border-gray-200 bg-white p-2 text-center">
          <button
            type="button"
            className="listing-table-show-more rounded border border-blue-300 bg-blue-50 px-4 py-1.5 text-sm font-semibold text-blue-700 hover:bg-blue-100"
            onClick={() => setShowAllRows((current) => !current)}
            aria-expanded={showAllRows}
          >
            {showAllRows
              ? "Show fewer listings"
              : `Show more listings (${hiddenRowCount})`}
          </button>
        </div>
      )}
    </div>
  );
};
